import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faCalendarAlt, faSyncAlt, faRoute, faMapMarkerAlt,
    faEnvelope, faLayerGroup, faClock, faExclamationTriangle,
    faSms, faPhoneVolume, faComments, faFileAlt, faIdCard, faBan,
    faUserShield, faFileInvoiceDollar, faUserCog, faHeadset, faPlug,
    faBook, faUserPlus, faUser,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AdminLayout from '../UI/Layout/AdminLayout';
import Axios from '../../axios/Axios';
import { userloginDetails } from '../Utils/AdminAuth';

const USER_JOURNEY_URL = 'https://stagnapi.authkey.io/api/v1/admin/UserJourney';

// Same resolved-category palette used on the Activity Log page, kept as its own
// copy here (rather than importing from Useractivitylog) since it's a plain
// lookup table, not shared logic — this page renders independently of that one.
const MODULE_STYLES = [
    { label: 'WhatsApp', match: /whatsapp|whastapp/i, bg: '#ecfdf5', color: '#0d9488', border: '#99f6e4', icon: faWhatsapp },
    { label: 'SMS', match: /sms/i, bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', icon: faSms },
    { label: 'Voice & TTS', match: /voice|flash_call|tts/i, bg: '#eef2ff', color: '#4f46e5', border: '#c7d2fe', icon: faPhoneVolume },
    { label: 'RCS', match: /rcs/i, bg: '#fdf4ff', color: '#c026d3', border: '#f5d0fe', icon: faComments },
    { label: 'Email', match: /email|^aws|aws_/i, bg: '#ecfeff', color: '#0891b2', border: '#a5f3fc', icon: faEnvelope },
    { label: 'Entity Management', match: /entity/i, bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', icon: faIdCard },
    { label: 'Template Management', match: /template/i, bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe', icon: faFileAlt },
    { label: 'Billing & Payments', match: /billing|transaction/i, bg: '#fffbeb', color: '#d97706', border: '#fde68a', icon: faFileInvoiceDollar },
    { label: 'Signup', match: /signup/i, bg: '#fdf2f8', color: '#db2777', border: '#fbcfe8', icon: faUserPlus },
    { label: 'Login & Security', match: /login|two_factor|change_password/i, bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', icon: faUserShield },
    { label: 'Profile & Notifications', match: /profile|notification/i, bg: '#f8fafc', color: '#334155', border: '#e2e8f0', icon: faUserCog },
    { label: 'Support', match: /ticket/i, bg: '#fff1f2', color: '#e11d48', border: '#fecdd3', icon: faHeadset },
    { label: 'Integrations & Widgets', match: /integration|widget|qr/i, bg: '#f0f9ff', color: '#0284c7', border: '#bae6fd', icon: faPlug },
    { label: 'API Docs', match: /api_docs/i, bg: '#fafaf9', color: '#57534e', border: '#e7e5e4', icon: faBook },
    { label: 'Optout', match: /optout/i, bg: '#fef2f2', color: '#dc2626', border: '#fecaca', icon: faBan },
    { label: 'Messaging', match: /^message$/i, bg: '#eef2ff', color: '#4f46e5', border: '#c7d2fe', icon: faComments },
];
const DEFAULT_MODULE_STYLE = { label: 'Other', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', icon: faLayerGroup };
const getModuleStyle = (module) => MODULE_STYLES.find((m) => m.match.test(module || '')) || DEFAULT_MODULE_STYLE;

const formatDateParam = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const parseDateParam = (str) => {
    if (!str) return null;
    const parts = str.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    const [y, m, d] = parts;
    return new Date(y, m - 1, d);
};

const parseLogDate = (str) => {
    if (!str) return null;
    const d = new Date(String(str).replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
};

// The journey API's per-event shape isn't documented (see flattenJourney below), so a
// field like `ip` or `message` can come back as an empty object instead of a string —
// rendering that directly as a JSX child crashes the whole page ("Objects are not valid
// as a React child"). Renderable fields are run through this first so a stray object
// is dropped instead of blowing up the timeline.
const safeText = (v) => (typeof v === 'string' || typeof v === 'number' ? v : '');

const DATE_PRESETS = [
    { key: 'today', label: 'Today', range: () => { const t = startOfDay(new Date()); return [t, t]; } },
    { key: 'yesterday', label: 'Yesterday', range: () => { const t = startOfDay(new Date()); const y = new Date(t); y.setDate(y.getDate() - 1); return [y, y]; } },
    { key: 'last7', label: 'Last 7 Days', range: () => { const t = startOfDay(new Date()); const s = new Date(t); s.setDate(s.getDate() - 6); return [s, t]; } },
    { key: 'last30', label: 'Last 30 Days', range: () => { const t = startOfDay(new Date()); const s = new Date(t); s.setDate(s.getDate() - 29); return [s, t]; } },
];

// The API's own response shape isn't documented, so this normalizes every shape
// it plausibly returns into one flat list of events: a flat array of events, an
// array of {module, events:[...]} groups (same convention as the Activity Log's
// own activityLog endpoint), or an object keyed by module name.
const flattenJourney = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw.flatMap((item) => {
            if (item && Array.isArray(item.events)) {
                return item.events.map((evt) => ({ ...evt, module: evt.module || item.module }));
            }
            return [item];
        });
    }
    if (typeof raw === 'object') {
        return Object.entries(raw).flatMap(([module, events]) => (
            Array.isArray(events) ? events.map((evt) => ({ ...evt, module: evt.module || module })) : []
        ));
    }
    return [];
};

const UserJourney = () => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const lStorage = userloginDetails();
    const adminToken = lStorage?.token;
    const userid = lStorage?.user?.userId;

    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [dateRange, setDateRange] = useState(() => {
        const from = parseDateParam(queryParams.get('fromdate'));
        const to = parseDateParam(queryParams.get('todate'));
        if (from && to) return [from, to];
        const today = startOfDay(new Date());
        return [today, today];
    });
    const [startDate, endDate] = dateRange;

    const [state, setState] = useState({ loading: false, error: '', events: [], userInfo: null });

    const activePresetKey = (() => {
        const s = formatDateParam(startDate);
        const e = formatDateParam(endDate);
        const match = DATE_PRESETS.find((p) => {
            const [ps, pe] = p.range();
            return formatDateParam(ps) === s && formatDateParam(pe) === e;
        });
        return match ? match.key : 'custom';
    })();

    const fetchJourney = async (overrides = {}) => {
        const start = overrides.start !== undefined ? overrides.start : startDate;
        const end = overrides.end !== undefined ? overrides.end : endDate;
        setState((prev) => ({ ...prev, loading: true, error: '' }));
        try {
            const { data } = await Axios.post(USER_JOURNEY_URL, {
                method: 'getUserJourney',
                user_id: userid,
                token: adminToken,
                id,
                // Sent alongside the single `date` field (kept for API compatibility with
                // the documented single-day contract) since this endpoint's admin sibling
                // (activityLog) accepts the same fromdate/todate pair for a ranged query.
                date: formatDateParam(end),
                fromdate: formatDateParam(start),
                todate: formatDateParam(end),
            });

            if (data.success === false) {
                setState({ loading: false, error: data.message || 'Failed to fetch user journey.', events: [], userInfo: null });
                return;
            }

            const events = flattenJourney(data.data ?? data.journey ?? data.result)
                .filter(Boolean)
                .sort((a, b) => (a.created_at || a.date || a.timestamp || '').localeCompare(b.created_at || b.date || b.timestamp || ''));

            setState({
                loading: false, error: '', events,
                userInfo: data.user || data.user_details || data.userInfo || null,
            });
        } catch (err) {
            setState({ loading: false, error: err.response?.data?.message || err.message, events: [], userInfo: null });
        }
    };

    useEffect(() => {
        fetchJourney();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const applyPreset = (preset) => {
        const [start, end] = preset.range();
        setDateRange([start, end]);
        fetchJourney({ start, end });
    };

    const handleFromDateChange = (date) => {
        setDateRange([date, endDate]);
        if (date && endDate) fetchJourney({ start: date, end: endDate });
    };

    const handleToDateChange = (date) => {
        setDateRange([startDate, date]);
        if (startDate && date) fetchJourney({ start: startDate, end: date });
    };

    const formatRangeLabel = () => {
        const opts = { day: 'numeric', month: 'short', year: 'numeric' };
        if (!startDate || !endDate) return '—';
        return formatDateParam(startDate) === formatDateParam(endDate)
            ? startDate.toLocaleDateString(undefined, opts)
            : `${startDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – ${endDate.toLocaleDateString(undefined, opts)}`;
    };

    // Groups the flat, ascending-sorted event list into day buckets so the
    // timeline can show a date divider once per day instead of repeating the
    // full date on every single card.
    const groupedByDay = useMemo(() => {
        const groups = new Map();
        state.events.forEach((evt) => {
            const raw = evt.created_at || evt.date || evt.timestamp || '';
            const day = raw.split(' ')[0].split('T')[0] || 'Unknown';
            if (!groups.has(day)) groups.set(day, []);
            groups.get(day).push(evt);
        });
        return Array.from(groups.entries());
    }, [state.events]);

    const summary = useMemo(() => {
        const modules = new Set(state.events.map((e) => getModuleStyle(e.module).label));
        const ips = new Set(state.events.map((e) => e.ip).filter(Boolean));
        const first = state.events[0];
        const last = state.events[state.events.length - 1];
        return {
            total: state.events.length,
            modules: modules.size,
            ips: ips.size,
            first: first ? (first.created_at || first.date || first.timestamp) : null,
            last: last ? (last.created_at || last.date || last.timestamp) : null,
        };
    }, [state.events]);

    // The journey API's `user` payload doesn't reliably carry an email (it's often
    // missing entirely), but the events themselves are the same shape as the Activity
    // Log rows, which do carry `email`/`username` — so fall back to whichever the
    // first event with one supplies.
    const userEmail = useMemo(() => {
        const fromUserInfo = safeText(state.userInfo?.email) || safeText(state.userInfo?.username);
        if (fromUserInfo) return fromUserInfo;
        const withEmail = state.events.find((e) => safeText(e.email) || safeText(e.username));
        return withEmail ? (safeText(withEmail.email) || safeText(withEmail.username)) : '';
    }, [state.userInfo, state.events]);

    const displayName = safeText(state.userInfo?.username) || safeText(state.userInfo?.email) || safeText(state.userInfo?.name) || `User #${id}`;

    const statChipStyle = {
        display: 'flex', alignItems: 'center', gap: '10px', background: '#fff',
        border: '1px solid #e8e8f0', borderRadius: '12px', padding: '14px 18px',
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)', flex: '1 1 200px',
    };

    return (
        <AdminLayout>
            <div style={{ paddingBottom: '0.75rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={() => (history.length > 2 ? history.goBack() : history.push('/admin/user-activity-log'))}
                        style={{
                            border: '1px solid #e2e8f0', background: '#fff', color: '#334155',
                            width: '36px', height: '36px', borderRadius: '10px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="Back"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <span style={{
                        width: '40px', height: '40px', borderRadius: '11px', flexShrink: 0,
                        background: 'linear-gradient(135deg, #6366f1, #4338ca)', color: '#fff', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '16px',
                        boxShadow: '0 4px 10px rgba(67,56,202,0.3)',
                    }}>
                        <FontAwesomeIcon icon={faRoute} />
                    </span>
                    <div>
                        <h4 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e', fontSize: '20px' }}>
                            {displayName} — Full Journey
                        </h4>
                        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <FontAwesomeIcon icon={faUser} style={{ fontSize: '11px' }} />
                                User ID: {id}
                            </span>
                            {userEmail && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '11px' }} />
                                    {userEmail}
                                </span>
                            )}
                        </p>
                    </div>
                </div>

                <div className="card" style={{ borderRadius: '12px', border: '1px solid #e8e8f0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-6 col-lg-2">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '5px', color: '#6b7280' }} />
                                    From Date
                                </label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={handleFromDateChange}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    maxDate={endDate || undefined}
                                    dateFormat="dd MMM yyyy"
                                    className="form-control"
                                />
                            </div>
                            <div className="col-6 col-lg-2">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '5px', color: '#6b7280' }} />
                                    To Date
                                </label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={handleToDateChange}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate || undefined}
                                    dateFormat="dd MMM yyyy"
                                    className="form-control"
                                />
                            </div>
                            <div className="col-12 col-lg-6">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Quick Range</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {DATE_PRESETS.map((preset) => (
                                        <button
                                            key={preset.key}
                                            type="button"
                                            onClick={() => applyPreset(preset)}
                                            style={{
                                                height: '32px', padding: '0 12px', borderRadius: '20px',
                                                fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                                                border: '1px solid', borderColor: activePresetKey === preset.key ? '#4f46e5' : '#e2e8f0',
                                                background: activePresetKey === preset.key ? '#4f46e5' : '#fff',
                                                color: activePresetKey === preset.key ? '#fff' : '#475569',
                                            }}
                                        >
                                            {preset.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="col-12 col-lg-2">
                                <button
                                    className="btn btn-primary w-100"
                                    onClick={() => fetchJourney()}
                                    disabled={state.loading}
                                    style={{ fontWeight: '600', fontSize: '13px' }}
                                >
                                    {state.loading ? <span className="spinner-border spinner-border-sm" /> : (
                                        <>
                                            <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
                                            Refresh
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {state.error && (
                    <div className="alert alert-danger" role="alert" style={{ marginTop: '16px' }}>
                        {state.error}
                    </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '16px' }}>
                    <div style={statChipStyle}>
                        <span style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#eef2ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={faLayerGroup} />
                        </span>
                        <div>
                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a2e' }}>{summary.total.toLocaleString()}</div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Total Events</div>
                        </div>
                    </div>
                    <div style={statChipStyle}>
                        <span style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#f0fdf4', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={faRoute} />
                        </span>
                        <div>
                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a2e' }}>{summary.modules}</div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Modules Touched</div>
                        </div>
                    </div>
                    <div style={statChipStyle}>
                        <span style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#ecfeff', color: '#0891b2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                        </span>
                        <div>
                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a2e' }}>{summary.ips}</div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Unique IPs</div>
                        </div>
                    </div>
                    <div style={statChipStyle}>
                        <span style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#fffbeb', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={faClock} />
                        </span>
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>{formatRangeLabel()}</div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Date Range</div>
                        </div>
                    </div>
                </div>

                <div style={{
                    background: '#fff', borderRadius: '12px', border: '1px solid #e8e8f0',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginTop: '16px', padding: '20px 24px',
                }}>
                    <h6 style={{ fontWeight: '700', color: '#1a1a2e', fontSize: '14px', marginBottom: '4px' }}>
                        Activity Timeline
                    </h6>
                    <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '18px' }}>
                        Every recorded action for this user across {formatRangeLabel()}, oldest first.
                    </p>

                    {state.loading ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                            <span className="spinner-border spinner-border-sm mr-2" /> Loading journey...
                        </div>
                    ) : groupedByDay.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: '13px' }}>
                            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '6px' }} />
                            No activity found for this user in the selected date range.
                        </div>
                    ) : (
                        <div style={{ position: 'relative', paddingLeft: '4px' }}>
                            {groupedByDay.map(([day, events]) => (
                                <div key={day} style={{ marginBottom: '22px' }}>
                                    <div style={{
                                        fontSize: '11px', fontWeight: '700', color: '#4338ca', background: '#eef2ff',
                                        display: 'inline-flex', borderRadius: '20px', padding: '4px 14px', marginBottom: '12px',
                                    }}>
                                        {(() => {
                                            const d = parseLogDate(day);
                                            return d ? d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : day;
                                        })()}
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderLeft: '2px solid #eef0f6', marginLeft: '9px', paddingLeft: '20px' }}>
                                        {events.map((evt, idx) => {
                                            const style = getModuleStyle(evt.module);
                                            const time = parseLogDate(evt.created_at || evt.date || evt.timestamp);
                                            return (
                                                <div key={idx} style={{
                                                    position: 'relative', background: '#fafbfd', border: '1px solid #f0f0f8',
                                                    borderRadius: '10px', padding: '12px 16px',
                                                }}>
                                                    <span style={{
                                                        position: 'absolute', left: '-28px', top: '16px',
                                                        width: '14px', height: '14px', borderRadius: '50%',
                                                        background: style.color, border: '3px solid #fff', boxShadow: `0 0 0 1px ${style.border}`,
                                                    }} />
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <span style={{
                                                                width: '26px', height: '26px', borderRadius: '8px', flexShrink: 0,
                                                                background: style.bg, color: style.color, display: 'flex',
                                                                alignItems: 'center', justifyContent: 'center', fontSize: '11px',
                                                                border: `1px solid ${style.border}`,
                                                            }}>
                                                                <FontAwesomeIcon icon={style.icon} />
                                                            </span>
                                                            <div>
                                                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e' }}>
                                                                    {safeText(evt.event) || safeText(evt.action) || style.label}
                                                                </div>
                                                                <div style={{ fontSize: '10px', fontWeight: '600', color: style.color }}>
                                                                    {style.label}{safeText(evt.page) ? ` · ${safeText(evt.page)}` : ''}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                            {time ? time.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}
                                                        </span>
                                                    </div>
                                                    {safeText(evt.message) && (
                                                        <div style={{ fontSize: '12px', color: '#334155', marginTop: '8px' }}>
                                                            {safeText(evt.message)}
                                                        </div>
                                                    )}
                                                    {safeText(evt.ip) && (
                                                        <div style={{ marginTop: '8px' }}>
                                                            <span style={{
                                                                fontSize: '11px', color: '#334155', fontWeight: '600',
                                                                fontFamily: 'Consolas, monospace', display: 'inline-flex',
                                                                alignItems: 'center', gap: '5px',
                                                                background: '#f0f0f8', padding: '3px 8px', borderRadius: '6px',
                                                            }}>
                                                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '9px', color: '#94a3b8' }} />
                                                                {safeText(evt.ip)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default UserJourney;
